/**
 * Why a card was declined: one plain line per reason (DNKADM-0466, contract DNKAPI-0676).
 *
 * dink-api maps the payment provider's decline code to a fixed `declineReason` and returns
 * it on:
 *  - the staff `402 PAYMENT_DECLINED` envelope (`declineReason`, present only when known;
 *    kept on `ApiError.details.declineReason` by `apiErrorFromBody`);
 *  - payment ledger rows (`GET /v2/admin/payments/transactions`);
 *  - member timeline payment items (`details.declineReason`);
 *  - admin pending charges and the reservation's inline pending-charge summary.
 *
 * It may be absent or null (dink does not know why), and `OTHER` means the provider gave a
 * code that has no line here. Both read as "no line": each surface keeps the copy it showed
 * before this change. An unknown future value is treated the same way.
 *
 * Provider text (fix-plain-card-errors): pay-api's own error sentences DO reach the admin.
 * dink-api passes a pay-api fault through as-is (DNKAPI-0349), so a failed card vault arrives
 * as `422 VAULT_FAILED` with `message: "Square rejected the card step: <SQUARE_CODE>"`. Staff
 * must never see that sentence or the code. {@link isProviderMessage} recognises it, and
 * {@link declineReasonFromMessage} reads the Square code out of it through the same table
 * dink-api uses (`DeclineReason.BY_SQUARE_CODE`), so the staff line is the same one a
 * `declineReason` would give. `serverMessage` drops any provider sentence at the seam.
 */
import { ApiError } from '@/api/errors';

export const DECLINE_REASONS = [
  'ZIP_MISMATCH',
  'CVV_MISMATCH',
  'EXPIRED',
  'INSUFFICIENT_FUNDS',
  'CALL_BANK',
  // DNKAPI-0706: Square's CARD_DECLINED_VERIFICATION_REQUIRED, the bank asking for 3-D Secure.
  'VERIFICATION_REQUIRED',
  'CARD_NOT_SUPPORTED',
  'LIMIT',
  'OTHER',
] as const;

export type DeclineReason = (typeof DECLINE_REASONS)[number];

/** The staff line per reason. `OTHER` has none on purpose: it never claims a specific cause. */
const DECLINE_REASON_LINES: Readonly<Record<Exclude<DeclineReason, 'OTHER'>, string>> = {
  ZIP_MISMATCH: "The billing zip code didn't match the card. Check the zip and try again.",
  CVV_MISMATCH: "The security code didn't match the card. Check the code and try again.",
  EXPIRED: 'The card is expired or the expiration date is wrong. Check the date or use another card.',
  INSUFFICIENT_FUNDS:
    "The card doesn't have enough available funds or credit for this amount. Try a smaller amount or another card.",
  CALL_BANK:
    'The bank declined this card. Ask the customer to call their bank or use another card.',
  // dink-api's own staff words for this reason (`DeclineReason.VERIFICATION_REQUIRED.staffText()`).
  VERIFICATION_REQUIRED:
    'The bank asked to verify this card; ask the member to add it from their phone or use another card.',
  CARD_NOT_SUPPORTED: "This type of card can't be used here. Ask the customer for another card.",
  // Covers both the seller's processing limit and the card issuer's, so it names neither.
  LIMIT: 'This amount is over a payment limit. Try a smaller amount or another card.',
};

/**
 * Square's error code to a reason: a copy of dink-api's `DeclineReason.BY_SQUARE_CODE`
 * (DNKAPI-0676, DNKAPI-0706). Keep the two in step.
 */
const DECLINE_REASON_BY_PROVIDER_CODE: Readonly<Record<string, Exclude<DeclineReason, 'OTHER'>>> = {
  ADDRESS_VERIFICATION_FAILURE: 'ZIP_MISMATCH',
  INVALID_POSTAL_CODE: 'ZIP_MISMATCH',
  CVV_FAILURE: 'CVV_MISMATCH',
  INVALID_CVV: 'CVV_MISMATCH',
  CARD_EXPIRED: 'EXPIRED',
  INVALID_EXPIRATION: 'EXPIRED',
  EXPIRATION_FAILURE: 'EXPIRED',
  BAD_EXPIRATION: 'EXPIRED',
  INSUFFICIENT_FUNDS: 'INSUFFICIENT_FUNDS',
  GENERIC_DECLINE: 'CALL_BANK',
  CARD_DECLINED: 'CALL_BANK',
  CARD_DECLINED_CALL_ISSUER: 'CALL_BANK',
  CARD_DECLINED_VERIFICATION_REQUIRED: 'VERIFICATION_REQUIRED',
  CARD_NOT_SUPPORTED: 'CARD_NOT_SUPPORTED',
  PAYMENT_LIMIT_EXCEEDED: 'LIMIT',
  TRANSACTION_LIMIT: 'LIMIT',
};

/** A Square error code: upper-case letters, digits and underscores (dink-api's `SQUARE_CODE`). */
const PROVIDER_CODE = /^[A-Z][A-Z0-9_]*$/;

/**
 * pay-api's provider sentences: "Square rejected the card step: CODE", "Stripe rejected the
 * subscription plan swap: CODE", "Square returned status 400". dink's own messages name
 * Square too ("Check the payment in Square before refunding"), so only these two verbs
 * directly after the provider's name count.
 */
const PROVIDER_SENTENCE = /\b(?:Square|Stripe) (?:rejected|returned)\b/;

/** The reason for one provider code: the mapped reason, `OTHER` for a code not in the table. */
function declineReasonFromProviderCode(code: string): DeclineReason {
  const reason = Object.hasOwn(DECLINE_REASON_BY_PROVIDER_CODE, code)
    ? DECLINE_REASON_BY_PROVIDER_CODE[code]
    : undefined;
  return reason ?? 'OTHER';
}

/**
 * The provider code a message carries, or null. Three shapes, as pay-api and dink-api write them:
 *  - a provider sentence: the code is the text after its last colon ("card step: CODE");
 *  - a pay-api `failureDetail`, "CODE: Square's message": the code before the first colon,
 *    read only when it is a code in the table (a dink sentence can start with a word and a colon);
 *  - a bare code, the whole message.
 */
export function providerCodeFromMessage(message: string | null | undefined): string | null {
  if (typeof message !== 'string') return null;
  const text = message.trim();
  if (text.length === 0) return null;
  if (PROVIDER_CODE.test(text)) return text;
  const colon = text.indexOf(':');
  if (colon < 0) return null;
  if (PROVIDER_SENTENCE.test(text)) {
    const tail = text.slice(text.lastIndexOf(':') + 1).trim();
    return PROVIDER_CODE.test(tail) ? tail : null;
  }
  const head = text.slice(0, colon).trim();
  return Object.hasOwn(DECLINE_REASON_BY_PROVIDER_CODE, head) ? head : null;
}

/** Whether a server message is the payment provider's text (or a bare code), never shown to staff. */
export function isProviderMessage(message: string | null | undefined): boolean {
  if (typeof message !== 'string') return false;
  return PROVIDER_SENTENCE.test(message) || providerCodeFromMessage(message) !== null;
}

/**
 * The reason a provider message names, or null when it is not provider text or carries no code.
 * A provider code not in the table reads `OTHER` (no line), like dink-api.
 */
export function declineReasonFromMessage(message: string | null | undefined): DeclineReason | null {
  if (!isProviderMessage(message)) return null;
  const code = providerCodeFromMessage(message);
  return code === null ? null : declineReasonFromProviderCode(code);
}

/**
 * pay-api's own `VAULT_FAILED` sentences that tell staff what to do
 * (`VaultCardService`). Each one is matched by a case-insensitive substring and reads a
 * short plain line that names no provider and never says "vault".
 */
const VAULT_SENTENCE_LINES: readonly (readonly [string, string])[] = [
  [
    'already on file for another payer',
    'This card is already saved for another customer. Use a different card.',
  ],
  [
    'cannot be stored again',
    'This card was removed earlier. Enter the card details again to add it back.',
  ],
  ['required to vault a card', "Add the member's name, email, or phone before saving a card."],
];

/** The plain line for one of pay-api's known vault sentences, or null for any other text. */
export function knownVaultSentenceLine(message: string | null | undefined): string | null {
  if (typeof message !== 'string') return null;
  const text = message.toLowerCase();
  for (const [needle, line] of VAULT_SENTENCE_LINES) {
    if (text.includes(needle)) return line;
  }
  return null;
}

/** A known `DeclineReason`, or null for a missing, null, blank or unknown value. */
export function parseDeclineReason(value: unknown): DeclineReason | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return (DECLINE_REASONS as readonly string[]).includes(text) ? (text as DeclineReason) : null;
}

/**
 * The staff line for a reason, or null when there is none (`OTHER`, null, unknown). The
 * caller shows its own existing decline copy on null.
 */
export function declineReasonLine(reason: DeclineReason | null | undefined): string | null {
  if (reason === null || reason === undefined || reason === 'OTHER') return null;
  return DECLINE_REASON_LINES[reason];
}

/**
 * Why a card error failed: dink-api's `details.declineReason` first; when that is absent or
 * `OTHER`, the Square code in a provider `details.message` (a passed-through pay-api fault,
 * e.g. `422 VAULT_FAILED`). Null when neither says.
 */
export function declineReasonFromError(error: unknown): DeclineReason | null {
  if (!(error instanceof ApiError)) return null;
  const sent = parseDeclineReason(error.details?.['declineReason']);
  if (sent !== null && sent !== 'OTHER') return sent;
  const message = error.details?.['message'];
  return declineReasonFromMessage(typeof message === 'string' ? message : null) ?? sent;
}

/** The reason line for a card error (see {@link declineReasonFromError}), or null. */
export function declineReasonLineFromError(error: unknown): string | null {
  return declineReasonLine(declineReasonFromError(error));
}
